import React from "react";
import { motion } from "framer-motion";
import FirstIMG from "../static/img/1.png";
import SecondIMG from "../static/img/2.png";
import InstagramLogo from "../static/img/instagram-icon.png";
import YoutubeLogo from "../static/img/youtube-icon.png";

const easing = [0.16, 1, 0.3, 1];

const cardVariant = {
  initial: { opacity: 0, y: 26 },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: easing },
  },
};

const headingVariant = {
  initial: {
    opacity: 0,
    x: -40,
    filter: "blur(12px)",
  },
  whileInView: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: easing },
  },
};

export default function HowItWorksSection() {
  return (
    <section className="md:pt-[0px] pt-[50px] "
      style={{
        width: "100%",
        minHeight: "10vh",
        marginTop: "10px",
        backgroundColor: "transparent",
        display: "flex",
        justifyContent: "center",
        boxSizing: "border-box",
        fontFamily:
          "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .hiw-container {
          max-width: 900px;
          width: 100%;
          padding-top: 0px
          margin: 0 auto;
          padding: 0 12px; /* small default side padding */
          box-sizing: border-box;
        }

        .hiw-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
        }

        /* MOBILE: make single-column, center cards and limit their width so
           there's visible space on left/right of the viewport */
        @media (max-width: 768px) {
          .hiw-container {
            max-width: 720px; /* constrain container on small screens */
            padding: 0 20px;  /* provide breathing room left/right */
          }

          .hiw-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            justify-items: center; /* center grid items */
          }

          /* Ensure each grid card doesn't stretch full width and stays centered */
          .hiw-grid > * {
            width: 100%;
            max-width: 520px;
            box-sizing: border-box;
          }
        }

        /* Responsive YouTube logo positioning */
        .yt-logo { --yt-top: 30px; }
        @media (max-width: 768px) {
          .yt-logo { --yt-top: 60px;}
        }
      `}</style>

      <div id="process" className="hiw-container">
        {/* Heading */}
        <motion.div
          variants={headingVariant}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, amount: 0.3 }}
          style={{ textAlign: "center", marginBottom: 34 }}
        >
          <div className="flex items-center justify-center gap-2 mb-[4px] border border-dashed border-neutral-300 shadow-sm h-[35px] w-[110px] mx-auto rounded-[40px] bg-white">
            <svg
              width="10"
              height="10"
              viewBox="0 0 256 256"
              fill="#3C5DF7"
              className="animate-spin"
              style={{ animationDuration: "5s" }}
            >
              <path d="M240,128a15.79,15.79,0,0,1-10.5,15l-63.44,23.07L143,229.5a16,16,0,0,1-30,0L89.94,166.06,26.5,143a16,16,0,0,1,0-30L89.94,89.94,113,26.5a16,16,0,0,1,30,0l23.07,63.44L229.5,113A15.79,15.79,0,0,1,240,128Z" />
            </svg>
            <span className="text-gray-700 font-medium text-[15px]">Process</span>
          </div>

          <h1 className="font-semibold"
            style={{
              margin: 0,
              fontSize: "50px",
              fontWeight: 700,
              letterSpacing: "-3px",

            }}
          >
            How It
            <span style={{ fontFamily: "Recoleta", color: "#3C5DF7" }}>
              {" "}
              Works?
            </span>
          </h1>

          <p
            
            style={{
              marginTop: 10,
              marginBottom: "47px",
              maxWidth: 420,
              marginInline: "auto",
              fontSize: 15.5,
              color: "#555",
              lineHeight: 1.6,
              fontWeight: 600,
            }}
          >
            A quick overview of how we work together to make your edit best in
            class!
          </p>
        </motion.div>

        {/* 4-grid layout */}
        <div className="hiw-grid">
          {/* GRID 1 */}
          <motion.article
            variants={cardVariant}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 22px 48px rgba(0,0,0,0.12)",
            }}
            style={cardBaseStyle}
          >
            <CardNumber>01</CardNumber>

            <div
              style={{
                marginBottom: 18,
                marginTop: 8,
                textAlign: "center",
              }}
            >
              <img
                src={FirstIMG}
                alt=""
                style={{ maxWidth: "82%", display: "inline-block" }}
              />
            </div>

            <h3 style={cardTitle}>Content Planning & Guidance</h3>
            <p style={cardBody}>
              Scripting, structure, and recording guidance for all video and podcast formats.
            </p>
          </motion.article>

          {/* GRID 2 */}
          <motion.article
            variants={cardVariant}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 22px 48px rgba(0,0,0,0.12)",
            }}
            style={cardBaseStyle}
          >
            <CardNumber>02</CardNumber>

            <div
              style={{
                position: "relative",
                marginBottom: 75,
                marginTop: 10,
                textAlign: "center",
                height: 120,
              }}
            >
              <motion.img
                src={SecondIMG}
                alt=""
                animate={{ scale: [1, 1.08, 1] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{ maxWidth: "86%", display: "inline-block" }}
              />
            </div>

            <h3 style={cardTitle}>Creator-Led Recording</h3>
            <p style={cardBody}>
                You record on your side with our remote support for lighting, audio, and framing.            </p>
          </motion.article>

          {/* GRID 3 */}
          <motion.article
            variants={cardVariant}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 22px 48px rgba(0,0,0,0.12)",
            }}
            style={{ ...cardBaseStyle, overflow: "visible" }}
          >
            <CardNumber>03</CardNumber>

            <div
              style={{
                position: "relative",
                marginBottom: "100px",
                marginTop: "25px",
                height: 110,
              }}
            >
              {/* avatar circle */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "20%",
                  transform: "translateX(-50%)",
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundImage:
                    'url("https://framerusercontent.com/images/Jzpb6S41NywURa1oRhUySeZD8.png")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 4,
                }}
              />

              {/* white bubble */}
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  background: "#f9f7f7ff",
                  padding: "8px 16px",
                  borderRadius: 10,
                  boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                  zIndex: 3,
                  border: "1px dashed rgba(0,0,0,0.08)",
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#222",
                    whiteSpace: "nowrap",
                  }}
                >
                  Requested a Revision
                </span>
              </div>

              {/* orange bar */}
              <div
                style={{
                  position: "absolute",
                  top: 50,
                  left: "50%",
                  transform: "translateX(-50%) rotate(-8deg)",
                  background: "linear-gradient(135deg, #ff844f, #3C5DF7)",
                  color: "#fff",
                  padding: "14px 20px",
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  textAlign: "center",
                  maxWidth: "200px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 18px 40px rgba(255, 107, 61, 0.35)",
                  zIndex: 1,
                }}
              >
                Revision is in progress!
              </div>
            </div>

            <h3 style={{ ...cardTitle, marginTop: 20, fontSize: 19 }}>
              Editing & Post-Production
            </h3>
            <p style={cardBody}>
              Clean cuts, colour correction, audio cleanup, captions, and platform-ready exports.
            </p>
          </motion.article>

          {/* GRID 4 — same size card, recreated layout */}
          <motion.article
            variants={cardVariant}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 22px 48px rgba(0,0,0,0.12)",
            }}
            style={cardBaseStyle}
          >
            <CardNumber>04</CardNumber>

            <div
              style={{
                position: "relative",
                height: 230,
                marginBottom: "5px",
              }}
            >
              {/* BLACK TAG — top right inside card */}
              <motion.div
                animate={{ y: [-2, 2, -2] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                  top: 35,
                  right: 44,
                  padding: "14px 28px",
                  background: "#000",
                  color: "#fff",
                  borderRadius: 32,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  rotate: "-6deg",
                  whiteSpace: "nowrap",
                  filter: "drop-shadow(0px 20px 40px rgba(0,0,0,0.18))",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Final_Cut.mp4
              </motion.div>

              {/* ORANGE PUBLISH — centered */}
              <motion.div
                animate={{ y: [-3, 3, -3], scale: [1, 1.03, 1] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                  top: 90,
                  left: "50%",
                  transform: "translateX(-50%)",
                  padding: "14px 36px",
                  fontSize: 16,
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #ff8a4f, #3C5DF7)",
                  color: "#fff",
                  borderRadius: 30,
                  whiteSpace: "nowrap",
                  rotate: "10deg",
                  boxShadow:
                    "0 0 70px rgba(255,110,40,0.45), 0 0 120px rgba(255,110,40,0.35), 0 10px 25px rgba(255,110,40,0.25)",
                }}
              >
                Publish
              </motion.div>

              {/* THUMBNAIL — bottom-left */}
              <motion.div
                animate={{ y: [3, -3, 3] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                  top: 115,
                  left: 96,
                  padding: "12px 24px",
                  borderRadius: 30,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  rotate: "-12deg",
                  transform: "rotate(-7deg)",
                  color: "#444",
                }}
              >
                <img
                  src={InstagramLogo}
                  alt="Instagram logo"
                  style={{ width: 70, height: 70, display: "inline-block" }}
                />
              </motion.div>
                            <motion.div
                              className="yt-logo"
                animate={{ y: [3, -3, 3] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                                top: "var(--yt-top)",
                  left: 26,
                  padding: "12px 24px",
                  borderRadius: 30,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  rotate: "-12deg",
                  transform: "rotate(-7deg)",
                  color: "#444",
                }}
              >
                <img
                  src={YoutubeLogo}
                  alt="YouTube logo"
                  style={{ width: 100, height: 100, display: "inline-block" }}
                />
              </motion.div>
            </div>

            <h3 style={{ ...cardTitle, marginTop: 0, fontSize: 19 }}>
              Distribution & YouTube 360°
            </h3>
            <p style={cardBody}>
               Uploads, SEO, thumbnails, scheduling, and community management.
            </p>
          </motion.article>
        </div>
      </div>
    </section>
  );
}

/* shared styles (JS only) */

const cardBaseStyle = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 26,
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.06)",
  padding: "26px 26px 22px",
  minHeight: 304,
  boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
  boxSizing: "border-box",
};

const cardTitle = {
  fontSize: 18.5,
  fontWeight: 600,
  margin: "0 0 6px",
};

const cardBody = {
  fontSize: 14.5,
  color: "#555",
  margin: 0,
  lineHeight: 1.6,
};

function CardNumber({ children }) {
  return (
    <div
      style={{
        position: "absolute",
        top: 10,
        left: 16,
        padding: "5px 10px",
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,0.06)",
        background: "#fff",
        fontSize: 12,
        fontWeight: 500,
        color: "#999",
      }}
    >
      {children}
    </div>
  );
}
